import React from "react";
import { colors, spacings } from "@ui/Theme";
import { AddCircleLine, ExternalLinkLine, InformationLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import t from "../../../../../i18n";
import { css, cx } from "@linaria/core";
import Button from "@ui/Button";
import tui from "@ui/i18n";
import InputV2 from "@ui/Input/InputV2";
import TextareaInput from "@ui/TextareaInput";
import Field from "@ui/Form/Field";
import useInput from "@ui/lib/hooks/useInput";
import { alphaNumRegexp } from "diagnostics-lib/io";
import {
  nonEmptyStringValidation,
  nullableNumberValidation,
  nullableStrictlyPositiveNumberValidation,
} from "../../../formValidationFunctions";
import { useQuantities, useQuantityIndex } from "../../../../../api/useQuantities";
import { MAX_NAME_LENGTH, MAX_UNIT_LENGTH } from "../../../../../domain/parser/quantity/csv/rules";
import ReactSelect from "@ui/ReactSelect";
import { useCanUserDo, useCanUserDoOnProject } from "../../../../canUserDo";
import { usePatch, usePost } from "../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { useEquipmentsInput } from "../../useEquipmentInput";
import { useEquipmentTypeInput } from "../../useEquipmentTypeInput";
import { useSystemsInput } from "../../useSystemInput";
import { getLabel } from "../view/Quantities/useMetadata";
import type { EditableQuantity } from "../view/Quantities/useMetadata";
import type { DependencyTree } from "../modalForm/ConfirmDelete";
import type { FormSignature } from "../view/Quantities";
import { useSetups } from "../../../../../api/useSetups";
import WarningEdit from "./WarningEdit";
import { DateTime } from "luxon";
import {
  content,
  content__formula,
  content__metrological,
  content__metrological_minAndMax,
  content__quantityDefinition,
  content__quantityDefinition_unitAndSystem,
  field,
  formulaHelpLink,
  inputFullWidth,
  MAX_DESCRIPTION_LENGTH,
  sectionTitle,
  submit,
  submit__action,
  submit__mandatory,
  submit__sup,
  unitInput,
} from "../CreateFormulaQuantity";
import Checkbox from "@ui/Checkbox";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { getCheckQuantityUsedInSetupsOrFormulas } from "diagnostics-domain/quantity/usage";
import { removeLineBreaks } from "diagnostics-lib/string";
import { ActionLink } from "@ui/Link";
import type { SidePanelView } from "../SidePanel";
import { FormulaField } from "../FormulaField";
import {
  buildFormulaVersionMutations,
  createDraftFormulaVersion,
  getDuplicateDraftFormulaVersionLocalIds,
  hasFormulaVersionDraftChanges,
  initializeDraftFormulaVersions,
  sortDraftFormulaVersions,
  type DraftFormulaVersion,
  updateDraftFormulaVersionFormula,
  updateDraftFormulaVersionStartsAt,
} from "../../../../../domain/formula-version";
import { getHelpCenterUrl } from "../../../../../lib/helpCenter";
import { translationLocale } from "../../../../../i18n/locale";
import { useQuantityFormulaVersions } from "../../../../../api/quantity/formula-version";
import useGroups from "../../../../../api/useGroups";
import formatDate from "@ui/i18n/formatDate";

interface Props {
  projectId: string;
  closePanel: () => void;
  timezone: string;
  quantity: EditableQuantity;
  className?: string;
  view: SidePanelView;
  onNameChange?: (name: string) => void;
}

export interface EditQuantityHandle {
  resetForm: () => void;
}
const EditQuantity = React.forwardRef<EditQuantityHandle, Props>(
  ({ projectId, timezone, closePanel, quantity, className, view, onNameChange }, ref) => {
    const { addToast } = useToasts();
    const quantities = useQuantities(projectId);
    const setups = useSetups(projectId);
    const groups = useGroups(projectId);
    const canUserModifyGlobalCategories = useCanUserDo("CATEGORY_EDIT");
    const canUserModifyCategories = useCanUserDoOnProject("PROJECT_CATEGORY_EDIT", projectId);
    const isUserSetuperOrAbove = useCanUserDoOnProject("PROJECT_SETUP", projectId);
    const quantityIndex = useQuantityIndex(projectId);
    const isAutoLockedAndCannotUnlock = quantity?.isAutoLocked;
    const userCanEdit = useCanUserDoOnProject("PROJECT_WRITE", projectId);
    const formulaHelpUrl = getHelpCenterUrl(
      translationLocale === "fr"
        ? "quantities#expressions-des-grandeurs-calculées"
        : "quantities#calculated-value-expressions",
      translationLocale,
    );

    // Setting this up to be able to warn about impacts on simulation and metrology
    const [editInformations, setEditInformations] = React.useState<{
      tree: DependencyTree | undefined;
      impactOnSimulation: boolean;
      hasImpactOnMetrology: boolean;
      values: FormSignature;
      quantity: EditableQuantity;
    }>({
      tree: undefined,
      impactOnSimulation: false,
      hasImpactOnMetrology: false,
      values: {},
      quantity: {} as EditableQuantity,
    });

    /** API */
    const validateFormula = usePost("/projects/:projectId(uuid)/quantities/formula/validate");

    const patchQuantity = usePatch("/projects/:projectId(uuid)/quantities/:quantityId(uuid)");

    /** Input */
    const initialNameValue = quantity ? quantity.name : "";
    const initialDescriptionValue = quantity ? (quantity.description ?? "") : "";
    const initialUnitValue = quantity ? (quantity.unit ?? "") : "";

    const {
      value: inputName,
      onChange: handleInputNameChange,
      setValue: setInputName,
      dirty: isInputNameDirty,
    } = useInput(initialNameValue);
    const {
      value: inputDescription,
      onChange: handleInputDescriptionChange,
      setValue: setInputDescription,
      dirty: isInputDescriptionDirty,
    } = useInput(initialDescriptionValue);
    const {
      value: inputUnit,
      onChange: handleInputUnitChange,
      setValue: setInputUnit,
      dirty: isInputUnitDirty,
    } = useInput(initialUnitValue);

    // Initialize inputs for systems, equipments, and equipment types
    const initialSystemInput = { systemId: quantity?.systemId ?? null };
    const systemInput = useSystemsInput(initialSystemInput);
    const initialEquipmentInput = {
      equipmentIds: quantity?.equipmentIds ?? [],
    };
    const equipmentsInput = useEquipmentsInput(projectId, [initialEquipmentInput]);
    const initialEquipmentType = {
      equipmentTypeId: quantity?.equipmentTypeId ?? null,
    };
    const equipmentType = useEquipmentTypeInput(initialEquipmentType);

    const initialSensorTagValue = quantity ? (quantity.sensor?.tag ?? "") : "";
    const {
      value: inputSensorTag,
      onChange: handleInputSensorTagChange,
      setValue: setInputSensorTag,
      dirty: isInputSensorTagDirty,
    } = useInput(initialSensorTagValue);

    // Use the quantity values as default values in edit mode, otherwise use empty strings
    const initialMinValue = quantity ? (quantity.minMetrology?.toString() ?? "") : "";
    const initialMaxValue = quantity ? (quantity.maxMetrology?.toString() ?? "") : "";
    const initialMaxStdValue = quantity ? (quantity.maxStdMetrology?.toString() ?? "") : "";
    const initialFormulaValue = quantity ? (quantity.formula ?? "") : "";

    // Initialize the inputs with the appropriate default values for dirty state tracking
    const {
      value: inputMin,
      onChange: handleInputMinChange,
      setValue: setInputMin,
      dirty: isMinDirty,
    } = useInput(initialMinValue);
    const {
      value: inputMax,
      onChange: handleInputMaxChange,
      setValue: setInputMax,
      dirty: isMaxDirty,
    } = useInput(initialMaxValue);
    const {
      value: inputMaxStd,
      onChange: handleInputMaxStdChange,
      setValue: setInputMaxStd,
      dirty: isMaxStdDirty,
    } = useInput(initialMaxStdValue);
    const {
      value: inputFormula,
      setValue: setInputFormula,
      dirty: isFormulaDirty,
    } = useInput(initialFormulaValue);

    React.useEffect(() => {
      onNameChange?.(inputName.trim() === "" ? quantity.name : inputName);
    }, [inputName, onNameChange, quantity.name]);

    const isInitiallyChecked = quantity ? quantity.locked && quantity.isAutoLocked : false;
    const [isFormulaInputChecked, setIsFormulaInputChecked] = React.useState(isInitiallyChecked);
    const [isIntermediateCalculation, setIsIntermediateCalculation] = React.useState(
      quantity.hidden,
    );

    const handleFormulaInputCheckboxChange = React.useCallback(() => {
      setIsFormulaInputChecked(prev => !prev);
    }, []);
    const handleIntermediateCalculationChange = React.useCallback(() => {
      setIsIntermediateCalculation(prev => !prev);
    }, []);

    // Update checkbox state when quantity changes
    React.useEffect(() => {
      if (quantity !== undefined) {
        const forceUnlock = !quantity.isAutoLocked && !quantity.locked ? true : false;
        setIsFormulaInputChecked(forceUnlock ? false : (quantity.locked ?? false));
        setIsIntermediateCalculation(quantity.hidden);
      }
    }, [quantity]);

    // Determine if this is a formula or measure quantity
    const isFormulaQuantity = quantity?.type === "FORMULA";
    const isMeasureQuantity = quantity?.type === "MEASURE";
    const quantityFormulaVersions = useQuantityFormulaVersions(projectId, quantity.id);
    const [draftFormulaVersions, setDraftFormulaVersions] = React.useState<DraftFormulaVersion[]>(
      () => initializeDraftFormulaVersions(quantityFormulaVersions),
    );
    const [deletedFormulaVersionIds, setDeletedFormulaVersionIds] = React.useState<string[]>([]);
    const initialDraftFormulaVersions = React.useMemo(
      () => sortDraftFormulaVersions(initializeDraftFormulaVersions(quantityFormulaVersions)),
      [quantityFormulaVersions],
    );
    const persistedFormulaVersionsById = React.useMemo(
      () =>
        new Map(
          quantityFormulaVersions.map(version => [
            version.id,
            {
              formula: version.formula,
              startsAt: version.startsAt,
            },
          ]),
        ),
      [quantityFormulaVersions],
    );
    const hasDraftFormulaVersionChanges = React.useMemo(
      () =>
        hasFormulaVersionDraftChanges({
          deletedFormulaVersionIds,
          draftFormulaVersions,
          persistedFormulaVersionsById,
        }),
      [deletedFormulaVersionIds, draftFormulaVersions, persistedFormulaVersionsById],
    );

    const visibleFormulaVersions = React.useMemo(
      () =>
        draftFormulaVersions.filter(
          version => !deletedFormulaVersionIds.includes(version.persistedId ?? ""),
        ),
      [deletedFormulaVersionIds, draftFormulaVersions],
    );
    const duplicateFormulaVersionLocalIds = React.useMemo(
      () => getDuplicateDraftFormulaVersionLocalIds(visibleFormulaVersions),
      [visibleFormulaVersions],
    );
    const hasDuplicateFormulaVersionStartsAt = duplicateFormulaVersionLocalIds.size > 0;

    const visibleFormulaVersionsOrderedForDisplay = React.useMemo(() => {
      const draftFormulaVersionsOnly = visibleFormulaVersions.filter(
        version => version.persistedId === null,
      );
      const persistedFormulaVersionsOnly = visibleFormulaVersions.filter(
        version => version.persistedId !== null,
      );

      return [...draftFormulaVersionsOnly.reverse(), ...persistedFormulaVersionsOnly];
    }, [visibleFormulaVersions]);

    const handleAddFormulaVersion = React.useCallback(() => {
      setDraftFormulaVersions(current => [
        ...current,
        createDraftFormulaVersion({ quantityId: quantity.id }),
      ]);
    }, [quantity.id]);

    const handleDeleteFormulaVersion = React.useCallback((localId: string) => {
      setDraftFormulaVersions(current => {
        const version = current.find(item => item.localId === localId);
        if (!version) return current;

        if (version.persistedId === null) {
          return current.filter(item => item.localId !== localId);
        }

        setDeletedFormulaVersionIds(ids =>
          ids.includes(version.persistedId ?? "") ? ids : [...ids, version.persistedId ?? ""],
        );

        return current;
      });
    }, []);

    const handleDraftFormulaChange = React.useCallback((localId: string, formula: string) => {
      setDraftFormulaVersions(current =>
        updateDraftFormulaVersionFormula(current, localId, formula),
      );
    }, []);

    const handleDraftStartsAtChange = React.useCallback(
      (localId: string, startsAt: Date | null) => {
        if (startsAt === null) return;
        const normalizedStartsAt = DateTime.fromJSDate(startsAt, {
          zone: timezone,
        })
          .startOf("day")
          .toJSDate();
        setDraftFormulaVersions(current =>
          updateDraftFormulaVersionStartsAt(current, localId, normalizedStartsAt),
        );
      },
      [timezone],
    );
    const cycleOptions = React.useMemo(
      () =>
        groups.map(group => ({
          value: `${group.title ?? "cycle"}-${group.startDate}`,
          name:
            group.title ??
            t("project.settings.quantity.side_panel.section.formula_input.unnamed_cycle"),
          startDateLabel: formatDate(new Date(group.startDate), "slash", timezone),
          startsAt: DateTime.fromMillis(group.startDate, { zone: timezone }).toJSDate(),
        })),
      [groups, timezone],
    );
    const setupOptions = React.useMemo(
      () =>
        setups
          .filter(setup => setup.startDate !== null)
          .map(setup => ({
            value: setup.id,
            name: setup.name,
            startDateLabel: formatDate(setup.startDate ?? "", "slash", timezone),
            startsAt: DateTime.fromISO(setup.startDate ?? "", { zone: timezone }).toJSDate(),
          })),
      [setups, timezone],
    );

    // Store the current quantity ID in a ref to track when it changes
    const quantityIdRef = React.useRef<string | undefined>(quantity?.id);

    React.useEffect(() => {
      setDraftFormulaVersions(initialDraftFormulaVersions);
      setDeletedFormulaVersionIds([]);
    }, [initialDraftFormulaVersions]);

    const isUnique = React.useCallback(
      (newName: string) => {
        // Special handling for edit mode: don't report name conflict with itself
        if (quantity && quantity.name === newName) {
          return true;
        }
        return !quantities.find(f => f.name === newName && f.id !== (quantity?.id ?? ""));
      },
      [quantities, quantity],
    );

    const nullForEmptyString = (s: string | undefined) => {
      if (s === "") return null;
      return s;
    };

    const parseFloatAndNullForEmptyString = (s: string | undefined): number | null | undefined => {
      if (s === "") return null;
      if (s === undefined) return undefined;
      const n = parseFloat(s);
      if (isNaN(n)) return undefined;
      return n;
    };

    const [formulaErrorsByVersionId, setFormulaErrorsByVersionId] = React.useState<
      Record<string, string>
    >({});
    const [formulaValidatingByVersionId, setFormulaValidatingByVersionId] = React.useState<
      Record<string, boolean>
    >({});

    const handleFormulaValidationErrorChange = React.useCallback(
      (versionId: string | null, error: string | undefined) => {
        const errorKey = versionId ?? "legacy";
        setFormulaErrorsByVersionId(current => {
          if (error === undefined) {
            if (current[errorKey] === undefined) {
              return current;
            }

            const { [errorKey]: _, ...rest } = current;
            return rest;
          }

          if (current[errorKey] === error) {
            return current;
          }

          return { ...current, [errorKey]: error };
        });
      },
      [],
    );
    const handleFormulaValidationPendingChange = React.useCallback(
      (versionId: string | null, isValidating: boolean | undefined) => {
        const validationKey = versionId ?? "legacy";
        setFormulaValidatingByVersionId(current => {
          if (isValidating === undefined || isValidating === false) {
            if (current[validationKey] === undefined) {
              return current;
            }

            const { [validationKey]: _, ...rest } = current;
            return rest;
          }

          if (current[validationKey] === isValidating) {
            return current;
          }

          return { ...current, [validationKey]: isValidating };
        });
      },
      [],
    );

    // Only initialize form fields when the quantity ID changes or component mounts
    React.useEffect(() => {
      // Check if quantity ID has changed or if component just mounted
      const quantityIdChanged = quantity?.id !== quantityIdRef.current;
      quantityIdRef.current = quantity?.id;

      if (quantityIdChanged) {
        // Reset the form modified state and editInformations
        equipmentType.resetValues();
        systemInput.resetValues();
        equipmentsInput.resetValues();
        setEditInformations({
          tree: undefined,
          impactOnSimulation: false,
          hasImpactOnMetrology: false,
          values: {},
          quantity: {} as EditableQuantity,
        });
      }

      if (quantity && quantityIdChanged) {
        setFormulaErrorsByVersionId({});
        setFormulaValidatingByVersionId({});
      }
    }, [equipmentType, equipmentsInput, quantity, systemInput]);

    /** Errors */
    const errorInputName = React.useMemo(() => {
      const emptyError = nonEmptyStringValidation(inputName);
      if (emptyError) return emptyError;
      if (!isUnique(inputName)) return t("errors.conflict");
      if (!inputName.match(alphaNumRegexp)) {
        return t("project.quantities.edit.name.error.format");
      }
      return;
    }, [inputName, isUnique]);

    const errorInputMin = React.useMemo(() => {
      const minIsNumberError = nullableNumberValidation(inputMin);
      if (!minIsNumberError && inputMax && isMinDirty) {
        const maxIsNumberError = nullableNumberValidation(inputMax);
        if (!maxIsNumberError && parseFloat(inputMin) >= parseFloat(inputMax)) {
          return t("setup.quantities.edit.min.superior.max.error");
        }
      }
      return minIsNumberError;
    }, [inputMin, inputMax, isMinDirty]);

    const errorInputMax = React.useMemo(() => {
      const maxIsNumberError = nullableNumberValidation(inputMax);
      if (!maxIsNumberError && inputMin && isMaxDirty) {
        const minIsNumberError = nullableNumberValidation(inputMin);
        if (!minIsNumberError && parseFloat(inputMin) >= parseFloat(inputMax)) {
          return t("setup.quantities.edit.max.inferior.min.error");
        }
      }
      return maxIsNumberError;
    }, [inputMin, inputMax, isMaxDirty]);

    const errorInputMaxStd = React.useMemo(() => {
      const maxStdIsNumberError = nullableStrictlyPositiveNumberValidation(inputMaxStd);
      if (maxStdIsNumberError) {
        return t("errors.field_must_be_strictly_positive_number");
      }
      return maxStdIsNumberError;
    }, [inputMaxStd]);

    // Use a single useEffect to compute impacts when relevant fields change
    React.useEffect(() => {
      if (quantity) {
        const currentValues: FormSignature = {
          min: inputMin,
          max: inputMax,
          maxStd: inputMaxStd,
          formula: inputFormula,
        };

        // Use the dirty input flags to determine if the relevant fields have changed
        const isWarningPossible =
          isFormulaDirty ||
          hasDraftFormulaVersionChanges ||
          isMinDirty ||
          isMaxDirty ||
          isMaxStdDirty;

        if (isWarningPossible) {
          // Calculate the impacts
          const minM = inputMin ? parseFloat(inputMin) : null;
          const maxM = inputMax ? parseFloat(inputMax) : null;
          const maxStdM = inputMaxStd ? parseFloat(inputMaxStd) : null;

          const hasImpactOnMetrology =
            minM !== quantity.minMetrology ||
            maxM !== quantity.maxMetrology ||
            maxStdM !== quantity.maxStdMetrology;

          if (isFormulaDirty || hasDraftFormulaVersionChanges || hasImpactOnMetrology) {
            const quantity_ = quantityIndex[quantity.id];
            if (quantity_ && quantity_.id) {
              const { hasImpactOnSetups, formulaDependencies } =
                getCheckQuantityUsedInSetupsOrFormulas(setups, quantity_);

              if (quantity.id === quantityIdRef.current) {
                setEditInformations(() => ({
                  impactOnSimulation: hasImpactOnSetups,
                  hasImpactOnMetrology,
                  tree: formulaDependencies,
                  values: currentValues,
                  quantity: quantity_,
                }));
              }
            }
          }
        } else {
          // Reset the edit information if all fields match the original values
          setEditInformations(() => ({
            impactOnSimulation: false,
            hasImpactOnMetrology: false,
            tree: undefined,
            values: {},
            quantity: {} as EditableQuantity,
          }));
        }
      }
    }, [
      quantity,
      inputMin,
      inputMax,
      inputMaxStd,
      inputFormula,
      isFormulaDirty,
      hasDraftFormulaVersionChanges,
      isMinDirty,
      isMaxDirty,
      isMaxStdDirty,
      projectId,
      quantityIdRef,
      quantityIndex,
      setups,
    ]);

    const resetForm = React.useCallback(() => {
      setInputName("");
      setInputDescription("");
      setInputUnit("");
      setInputMin("");
      setInputMax("");
      setInputMaxStd(""); // Added for measure quantities
      setInputFormula("");
      setInputSensorTag(""); // Added for measure quantities
      setDraftFormulaVersions(initializeDraftFormulaVersions(quantityFormulaVersions));
      setDeletedFormulaVersionIds([]);
      setFormulaErrorsByVersionId({});
      setFormulaValidatingByVersionId({});
      systemInput.resetValues();
      equipmentsInput.resetValues();
      equipmentType.resetValues();
    }, [
      setInputName,
      setInputDescription,
      setInputUnit,
      setInputMin,
      setInputMax,
      setInputMaxStd,
      setInputFormula,
      setInputSensorTag,
      quantityFormulaVersions,
      systemInput,
      equipmentsInput,
      equipmentType,
    ]);

    React.useImperativeHandle(ref, () => ({
      resetForm,
    }));

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleSubmit = React.useCallback(async () => {
      if (!quantity) {
        console.debug("No quantity provided for editing, cannot submit.");
        return;
      }

      const quantityId = quantity.id;
      const name = nullForEmptyString(inputName);
      const formula = nullForEmptyString(removeLineBreaks(inputFormula));
      const description = nullForEmptyString(removeLineBreaks(inputDescription));
      const unit = nullForEmptyString(inputUnit);
      const minMetrology = parseFloatAndNullForEmptyString(inputMin);
      const maxMetrology = parseFloatAndNullForEmptyString(inputMax);
      const maxStdMetrology = parseFloatAndNullForEmptyString(inputMaxStd);
      const systemId = systemInput.selectedSystem?.value ?? null;
      const equipmentTypeId = equipmentType.selectedEquipmentType?.value ?? null;
      const equipmentIds = equipmentsInput.selectedEquipments
        .map(e => e.value)
        .filter((id): id is string => id !== null);
      const formulaVersionMutations = buildFormulaVersionMutations({
        deletedFormulaVersionIds,
        draftFormulaVersions,
        persistedFormulaVersionsById,
      });

      if (name === null) {
        throw new Error("name validation failed in table, name is required");
      }

      const { status, body: formulaValidation } = formula
        ? await validateFormula({
            body: { id: quantity.id, formula },
            routeParams: {
              projectId,
            },
          })
        : ({ status: 204, body: undefined } as const);

      switch (status) {
        case 204:
          await patchQuantity({
            body: {
              name,
              formula,
              ...(isFormulaQuantity && isUserSetuperOrAbove && { locked: isFormulaInputChecked }),
              ...(isUserSetuperOrAbove && { hidden: isIntermediateCalculation }),
              description,
              unit,
              minMetrology,
              maxMetrology,
              maxStdMetrology,
              systemId,
              equipmentTypeId,
              equipmentIds,
              ...(formulaVersionMutations.hasMutations && {
                formulaVersions: {
                  ...(formulaVersionMutations.create.length > 0 && {
                    create: formulaVersionMutations.create,
                  }),
                  ...(formulaVersionMutations.update.length > 0 && {
                    update: formulaVersionMutations.update,
                  }),
                  ...(formulaVersionMutations.delete.length > 0 && {
                    delete: formulaVersionMutations.delete,
                  }),
                },
              }),
            },
            routeParams: { projectId, quantityId },
          });

          addToast("success", t("project.quantities.edit.success"));
          closePanel();
          resetForm();
          break;
        case 417: {
          addToast("error", formulaValidation);
          break;
        }
        default:
          addToast("error", t("project.quantities.edit.error"));
          throw new Error(status satisfies never);
      }
    }, [
      quantity,
      inputName,
      inputFormula,
      inputDescription,
      inputUnit,
      inputMin,
      inputMax,
      inputMaxStd,
      deletedFormulaVersionIds,
      draftFormulaVersions,
      persistedFormulaVersionsById,
      systemInput.selectedSystem?.value,
      equipmentType.selectedEquipmentType?.value,
      equipmentsInput.selectedEquipments,
      validateFormula,
      projectId,
      patchQuantity,
      // revalidateQuantityFormulaVersions,
      isFormulaInputChecked,
      isIntermediateCalculation,
      closePanel,
      resetForm,
      addToast,
      isFormulaQuantity,
      isUserSetuperOrAbove,
    ]);

    const dirty = React.useMemo(() => {
      if (!inputName || inputName.trim() === "") {
        return false;
      }

      if (!quantity) return false;

      const originalSystemId = quantity.systemId ?? null;
      const currentSystemId = systemInput.selectedSystem?.value ?? null;
      const hasSystemChanged = currentSystemId !== originalSystemId;

      // Fix equipment type comparison by handling null and undefined values
      const originalEquipmentTypeId =
        quantity.equipmentTypeId === undefined ? null : quantity.equipmentTypeId;
      const currentEquipmentTypeId =
        equipmentType.selectedEquipmentType?.value === undefined
          ? null
          : equipmentType.selectedEquipmentType?.value;
      const hasEquipmentTypeChanged = originalEquipmentTypeId !== currentEquipmentTypeId;

      // Check if equipment IDs have changed
      const originalEquipmentIds = quantity.equipmentIds ?? [];
      const currentEquipmentIds = equipmentsInput.selectedEquipments
        .map(e => e.value)
        .filter((id): id is string => id !== null);
      const hasEquipmentsChanged =
        originalEquipmentIds.length !== currentEquipmentIds.length ||
        !originalEquipmentIds.every(id => currentEquipmentIds.includes(id)) ||
        !currentEquipmentIds.every(id => originalEquipmentIds.includes(id));
      const hasLockChanged =
        isFormulaQuantity && isUserSetuperOrAbove && quantity.locked !== isFormulaInputChecked;
      const hasIntermediateCalculationChanged =
        isUserSetuperOrAbove && quantity.hidden !== isIntermediateCalculation;
      const hasFormulaVersionsChanged = hasDraftFormulaVersionChanges;

      // Form is dirty if any field has changed
      const hasChanges =
        isFormulaDirty ||
        isMinDirty ||
        isMaxDirty ||
        isMaxStdDirty ||
        isInputNameDirty ||
        isInputDescriptionDirty ||
        isInputUnitDirty ||
        isInputSensorTagDirty ||
        hasEquipmentTypeChanged ||
        hasSystemChanged ||
        hasEquipmentsChanged ||
        hasLockChanged ||
        hasIntermediateCalculationChanged ||
        hasFormulaVersionsChanged;

      return hasChanges;
    }, [
      inputName,
      quantity,
      systemInput.selectedSystem?.value,
      equipmentType.selectedEquipmentType?.value,
      equipmentsInput.selectedEquipments,
      isFormulaQuantity,
      isUserSetuperOrAbove,
      isFormulaInputChecked,
      isIntermediateCalculation,
      isFormulaDirty,
      isMinDirty,
      isMaxDirty,
      isMaxStdDirty,
      isInputNameDirty,
      isInputDescriptionDirty,
      isInputUnitDirty,
      isInputSensorTagDirty,
      hasDraftFormulaVersionChanges,
    ]);

    const isValid =
      !errorInputName &&
      !errorInputMin &&
      !errorInputMax &&
      !errorInputMaxStd &&
      Object.keys(formulaErrorsByVersionId).length === 0 &&
      !hasDuplicateFormulaVersionStartsAt;
    const hasValidatingFormula = Object.values(formulaValidatingByVersionId).some(Boolean);

    const isFormulaInputDisabled =
      (!isUserSetuperOrAbove && quantity?.locked) ||
      (isAutoLockedAndCannotUnlock && !isUserSetuperOrAbove);

    return (
      <>
        <div className={cx(content, className)}>
          {view === "general-information" && (
            <>
              <section className={content__quantityDefinition}>
                <Text className={sectionTitle}>
                  {t("project.settings.quantity.side_panel.section.definition_quantity.title")}
                </Text>

                {isUserSetuperOrAbove && (
                  <Checkbox
                    checked={isIntermediateCalculation}
                    onChange={handleIntermediateCalculationChange}
                    disabled={!userCanEdit}
                  >
                    <span className={checkboxSpan}>
                      {t(
                        "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation",
                      )}
                      <TooltipV2
                        overlay={
                          <OverlayWrapper
                            className={styledOverlayWrapper}
                            text={t(
                              "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation_tooltip",
                            )}
                          />
                        }
                      >
                        <span className={tooltipSpan}>
                          <InformationLine size="m" color={colors.carbon800} />
                        </span>
                      </TooltipV2>
                    </span>
                  </Checkbox>
                )}

                <InputV2
                  className={inputFullWidth}
                  label={getLabel("name")}
                  value={inputName}
                  defaultValue={initialNameValue}
                  onChange={handleInputNameChange}
                  error={isInputNameDirty ? errorInputName : undefined}
                  dirty={isInputNameDirty}
                  maxLength={MAX_NAME_LENGTH}
                  required
                  disabled={!userCanEdit}
                />

                <Field
                  id=""
                  label={getLabel("description")}
                  className={field}
                  revertable={isInputDescriptionDirty}
                  revert={() => setInputDescription(initialDescriptionValue)}
                  disabled={!userCanEdit}
                >
                  <TextareaInput
                    value={inputDescription}
                    onChange={handleInputDescriptionChange}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    minHeight={56}
                    enforceMaxLength
                    showCharCounter={false}
                  />
                </Field>

                {/* Sensor tag input, only for measure quantities */}
                {isMeasureQuantity && (
                  <InputV2
                    className={inputFullWidth}
                    label={getLabel("sensorTag")}
                    value={inputSensorTag}
                    onChange={handleInputSensorTagChange}
                    maxLength={MAX_UNIT_LENGTH}
                    disabled
                  />
                )}

                <div className={content__quantityDefinition_unitAndSystem}>
                  <InputV2
                    className={unitInput}
                    label={getLabel("unit")}
                    value={inputUnit}
                    defaultValue={initialUnitValue}
                    dirty={isInputUnitDirty}
                    onChange={handleInputUnitChange}
                    maxLength={MAX_UNIT_LENGTH}
                    disabled={!userCanEdit}
                  />

                  <Field
                    id=""
                    label={getLabel("system")}
                    className={field}
                    revertable={systemInput.dirty}
                    revert={() => systemInput.resetValues()}
                    disabled={!userCanEdit}
                  >
                    <ReactSelect
                      menuPortalTarget={window.document.body}
                      placeholder={t("common.select")}
                      value={systemInput.selectedSystem}
                      options={systemInput.systemOptions}
                      onChange={systemInput.handleSystemChanged}
                      onCreate={
                        canUserModifyGlobalCategories ? systemInput.handleSystemCreated : undefined
                      }
                      onEdit={
                        canUserModifyGlobalCategories ? systemInput.handleSystemEdited : undefined
                      }
                      onDelete={
                        canUserModifyGlobalCategories ? systemInput.handleSystemDeleted : undefined
                      }
                    />
                  </Field>
                </div>

                <Field
                  id=""
                  label={getLabel("equipments")}
                  className={field}
                  revertable={equipmentsInput.dirty}
                  revert={() => equipmentsInput.resetValues()}
                  disabled={!userCanEdit}
                >
                  <ReactSelect
                    menuPortalTarget={window.document.body}
                    isMulti
                    placeholder={t("common.select")}
                    value={equipmentsInput.selectedEquipments}
                    options={equipmentsInput.equipmentOptions}
                    onChange={equipmentsInput.handleEquipmentChanged}
                    onCreate={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentCreated : undefined
                    }
                    onEdit={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentEdited : undefined
                    }
                    onDelete={
                      canUserModifyCategories ? equipmentsInput.handleEquipmentDeleted : undefined
                    }
                  />
                </Field>

                <Field
                  id=""
                  label={getLabel("equipmentType")}
                  className={field}
                  revertable={equipmentType.dirty}
                  revert={() => equipmentType.resetValues()}
                  disabled={!userCanEdit}
                >
                  <ReactSelect
                    placeholder={t("common.select")}
                    menuPortalTarget={window.document.body}
                    value={equipmentType.selectedEquipmentType}
                    options={equipmentType.equipmentTypeOptions}
                    onChange={equipmentType.handleEquipmentTypeChanged}
                    onCreate={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeCreated
                        : undefined
                    }
                    onEdit={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeEdited
                        : undefined
                    }
                    onDelete={
                      canUserModifyGlobalCategories
                        ? equipmentType.handleEquipmentTypeDeleted
                        : undefined
                    }
                  />
                </Field>
              </section>

              {/* Metrology section */}
              <section className={content__metrological}>
                <Text className={sectionTitle}>
                  {t("project.settings.quantity.side_panel.section.metrological_filters.title")}
                </Text>

                <div className={content__metrological_minAndMax}>
                  <InputV2
                    className={inputFullWidth}
                    label={getLabel("min")}
                    type="number"
                    step={1}
                    value={inputMin}
                    defaultValue={initialMinValue}
                    dirty={isMinDirty}
                    onChange={handleInputMinChange}
                    error={errorInputMin}
                    disabled={!userCanEdit}
                  />
                  <InputV2
                    className={inputFullWidth}
                    label={getLabel("max")}
                    type="number"
                    step={1}
                    value={inputMax}
                    defaultValue={initialMaxValue}
                    dirty={isMaxDirty}
                    onChange={handleInputMaxChange}
                    error={errorInputMax}
                    disabled={!userCanEdit}
                  />
                  {isMeasureQuantity && (
                    <InputV2
                      className={inputFullWidth}
                      label={getLabel("maxStd")}
                      type="number"
                      step={1}
                      value={inputMaxStd}
                      defaultValue={initialMaxStdValue}
                      dirty={isMaxStdDirty}
                      onChange={handleInputMaxStdChange}
                      error={errorInputMaxStd}
                      disabled={!userCanEdit}
                    />
                  )}
                </div>
              </section>
            </>
          )}
          {view === "formulae" && isFormulaQuantity && (
            <section className={content__formula}>
              <ActionLink
                className={formulaHelpLink}
                href={formulaHelpUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLinkLine />
                {t("project.settings.quantity.side_panel.section.formula_input.help_link")}
              </ActionLink>
              <Button
                style={{ width: "max-content" }}
                variant="link"
                leftIcon={<AddCircleLine />}
                disabled={isFormulaInputDisabled}
                onClick={handleAddFormulaVersion}
              >
                {t("project.settings.quantity.side_panel.tab.formulae.add")}
              </Button>

              {isUserSetuperOrAbove && !quantity.isAutoLocked && (
                <Checkbox
                  checked={isFormulaInputChecked}
                  onChange={handleFormulaInputCheckboxChange}
                  disabled={!isUserSetuperOrAbove}
                >
                  <span className={checkboxSpan}>
                    {t("project.settings.quantity.side_panel.section.formula_input.lock_title")}
                    <TooltipV2
                      overlay={
                        <OverlayWrapper
                          className={styledOverlayWrapper}
                          text={t(
                            "project.settings.quantity.side_panel.section.formula_input.lock_tooltip",
                          )}
                        />
                      }
                    >
                      <span className={tooltipSpan}>
                        <InformationLine size="m" color={colors.carbon800} />
                      </span>
                    </TooltipV2>
                  </span>
                </Checkbox>
              )}
              {isAutoLockedAndCannotUnlock ? (
                <div className={lockedInfoContainer}>
                  <div className={lockedInfoLine}>
                    <InformationLine size="m" color={colors.darkNitrogen600} />
                    <Text type="body2" className={lockedInfoText}>
                      {t(
                        "project.settings.quantity.side_panel.section.formula_input.locked_due_to_dependency",
                      )}
                    </Text>
                  </div>
                </div>
              ) : (
                !isUserSetuperOrAbove &&
                quantity?.locked && (
                  <div className={lockedInfoContainer}>
                    <div className={lockedInfoLine}>
                      <InformationLine size="m" color={colors.darkNitrogen600} />
                      <Text type="body2" className={lockedInfoText}>
                        {t(
                          "project.settings.quantity.side_panel.section.formula_input.locked_warning",
                        )}
                      </Text>
                    </div>
                  </div>
                )
              )}

              {visibleFormulaVersionsOrderedForDisplay.map((fv, i, arr) => (
                <FormulaField
                  key={fv.localId}
                  versionId={fv.localId}
                  index={arr.length - i + 1}
                  quantityId={fv.quantityId}
                  projectId={projectId}
                  timezone={timezone}
                  formula={fv.formula}
                  startsAt={fv.startsAt}
                  endsAt={arr[i - 1]?.startsAt ?? null}
                  onFormulaChange={handleDraftFormulaChange}
                  onStartsAtChange={handleDraftStartsAtChange}
                  onStartsAtFillFromCycle={handleDraftStartsAtChange}
                  onStartsAtFillFromSetup={handleDraftStartsAtChange}
                  onDelete={handleDeleteFormulaVersion}
                  cycleOptions={cycleOptions}
                  setupOptions={setupOptions}
                  onValidationErrorChange={handleFormulaValidationErrorChange}
                  onValidationPendingChange={handleFormulaValidationPendingChange}
                  disabled={isFormulaInputDisabled}
                  deleteDisabled={isFormulaInputDisabled}
                  required={!isMeasureQuantity}
                  startsAtError={
                    duplicateFormulaVersionLocalIds.has(fv.localId)
                      ? t(
                          "project.settings.quantity.side_panel.section.formula_input.duplicate_usage_period_error",
                        )
                      : undefined
                  }
                />
              ))}
              {quantity.type === "FORMULA" && quantity.formula && (
                <FormulaField
                  versionId={null}
                  quantityId={quantity.id}
                  projectId={projectId}
                  index={1}
                  formula={inputFormula}
                  startsAt={null}
                  timezone={timezone}
                  endsAt={visibleFormulaVersionsOrderedForDisplay.at(-1)?.startsAt ?? null}
                  legacyFormula
                  onFormulaChange={(_, formula) => setInputFormula(formula)}
                  onValidationErrorChange={handleFormulaValidationErrorChange}
                  onValidationPendingChange={handleFormulaValidationPendingChange}
                  disabled={isFormulaInputDisabled}
                  required={!isMeasureQuantity}
                />
              )}

              {/*<Field
                id=""
                label={t("project.settings.quantity.side_panel.section.formula_input.input_label")}
                className={field}
                required={!isMeasureQuantity}
              >
                <TextareaInput
                  value={inputFormula}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    handleInputFormula(e);
                    textAreaAsyncValidation.handleChange(e);
                  }}
                  onBlur={textAreaAsyncValidation.handleBlur}
                  onFocus={textAreaAsyncValidation.handleFocus}
                  error={textAreaAsyncValidation.error}
                  maxLength={MAX_FORMULA_LENGTH}
                  minHeight={79}
                  enforceMaxLength={false}
                  showCharCounter={false}
                  disabled={isFormulaInputDisabled}
                  textareaClassName={disabledTextareaStyle}
                />
              </Field>*/}
            </section>
          )}
        </div>

        {/* Warning section for the quantity edition when min/max/std or formula is edited */}
        {(() => {
          const baseFormulaChanged = Boolean(
            editInformations.quantity.formula &&
              editInformations.values.formula &&
              editInformations.quantity.formula !== editInformations.values.formula,
          );
          const formulaChanged = baseFormulaChanged || hasDraftFormulaVersionChanges;

          const hasTree = editInformations.tree && Object.keys(editInformations.tree).length > 0;

          // Check if any of the relevant fields is dirty
          const isAnyFieldDirty =
            isFormulaDirty ||
            hasDraftFormulaVersionChanges ||
            isMinDirty ||
            isMaxDirty ||
            isMaxStdDirty;
          const shouldShowWarning = isAnyFieldDirty && hasTree;

          if (shouldShowWarning) {
            return (
              <div className={warningEditContainer}>
                <WarningEdit
                  setups={setups}
                  formulaDependencies={editInformations.tree || { id: "", name: "", trees: [] }}
                  quantities={quantities}
                  hasImpactOnMetrology={editInformations.hasImpactOnMetrology}
                  hasImpactOnFormula={formulaChanged}
                />
              </div>
            );
          }

          return null;
        })()}

        {/* Submit */}
        <div className={submit}>
          <div className={submit__mandatory}>
            <sup className={submit__sup}>*</sup> {tui("input.mandatory")}
          </div>
          <div className={submit__action}>
            <Button
              onClick={() => {
                closePanel();
                resetForm();
              }}
            >
              {t("commons.message_card.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!dirty || !isValid || isSubmitting || hasValidatingFormula}
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  await handleSubmit();
                } finally {
                  setIsSubmitting(false);
                }
              }}
              title={dirty ? undefined : "No changes detected"}
            >
              {t("commons.button.save")}
            </Button>
          </div>
        </div>
      </>
    );
  },
);

export default EditQuantity;

//#region CSS
const warningEditContainer = css`
  position: sticky;
  bottom: 73px; // height of the submit section
  z-index: 1;
  background-color: ${colors.titanium50}; /* Or your desired background */
`;

const checkboxSpan = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const tooltipSpan = css`
  outline: none;
  display: flex;
  align-items: center;
`;

const styledOverlayWrapper = css`
  max-width: 200px;
`;

const lockedInfoContainer = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};
  padding-bottom: 0 0;
  align-self: stretch;
`;

const lockedInfoLine = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacings.xxs};
  align-self: stretch;
`;

const lockedInfoText = css`
  flex: 1 0 0;
  color: ${colors.darkNitrogen600};
`;
//#endregion CSS
